import type {
  CustomerFinanceBreakdown,
  CustomerPricingBreakdown,
  Instalments,
} from "@ir/types";

export const CUSTOMER_PRICING_RULES = {
  iptRate: 0.12,
  motorLegalFee: 10,
  arrangementFee: 130,
  instalmentDepositRate: 0.2,
  instalmentTransactionFeeRate: 0.1493,
  financeCommissionRate: 0.1014,
  providers: {
    granite: {
      label: "Granite",
      insurerFeeBeforeCommission: 0,
      feeInLieuRate: 0.15,
      displayedStandardCommissionRate: 0,
      salesIncomeCommissionRate: 0,
    },
    inshur: {
      label: "INSHUR",
      insurerFeeBeforeCommission: 100,
      feeInLieuRate: 0.05,
      displayedStandardCommissionRate: 0.1,
      salesIncomeCommissionRate: 0.1,
    },
    kinetiq: {
      label: "Kinetiq",
      insurerFeeBeforeCommission: 0,
      feeInLieuRate: 0.08,
      displayedStandardCommissionRate: 0.07,
      salesIncomeCommissionRate: 0.07,
    },
  },
} as const;

type CustomerPricingProvider = keyof typeof CUSTOMER_PRICING_RULES.providers;

export function isCustomerPricingProvider(
  provider: string | null | undefined,
): provider is CustomerPricingProvider {
  return Object.hasOwn(CUSTOMER_PRICING_RULES.providers, normaliseProvider(provider));
}

export function normaliseProvider(provider: string | null | undefined): string {
  return (provider ?? "").trim().toLowerCase();
}

export function calculateCustomerPricing(
  provider: string,
  insurerPremium: number,
  instalments?: Instalments | null,
): CustomerPricingBreakdown {
  const normalizedProvider = normaliseProvider(provider);
  const rule = isCustomerPricingProvider(normalizedProvider)
    ? CUSTOMER_PRICING_RULES.providers[normalizedProvider]
    : {
        label: provider,
        insurerFeeBeforeCommission: 0,
        feeInLieuRate: 0,
        displayedStandardCommissionRate: 0,
        salesIncomeCommissionRate: 0,
      };

  const premium = roundMoney(insurerPremium);
  const feeInLieuAmount = roundMoney(premium * rule.feeInLieuRate);
  const totalSellingPrice = roundMoney(
    premium +
      CUSTOMER_PRICING_RULES.motorLegalFee +
      CUSTOMER_PRICING_RULES.arrangementFee +
      feeInLieuAmount,
  );
  const feesIncome = roundMoney(CUSTOMER_PRICING_RULES.arrangementFee + feeInLieuAmount);
  const finance = instalments ? calculateFinance(totalSellingPrice, instalments) : null;
  const financeCommission = finance
    ? roundMoney(finance.loanAmount * CUSTOMER_PRICING_RULES.financeCommissionRate)
    : 0;
  const commissionablePremium = Math.max(0, premium - rule.insurerFeeBeforeCommission);
  const premiumCommission = roundMoney(
    (commissionablePremium / (1 + CUSTOMER_PRICING_RULES.iptRate)) *
      rule.salesIncomeCommissionRate,
  );

  return {
    provider: normalizedProvider,
    insurerPremium: premium,
    insurerFeeBeforeCommission: rule.insurerFeeBeforeCommission,
    iptRate: CUSTOMER_PRICING_RULES.iptRate,
    displayedStandardCommissionRate: rule.displayedStandardCommissionRate,
    salesIncomeCommissionRate: rule.salesIncomeCommissionRate,
    motorLegalFee: CUSTOMER_PRICING_RULES.motorLegalFee,
    arrangementFee: CUSTOMER_PRICING_RULES.arrangementFee,
    feeInLieuRate: rule.feeInLieuRate,
    feeInLieuAmount,
    totalSellingPrice,
    premiumCommission,
    feesIncome,
    financeCommission,
    totalSalesIncome: roundMoney(premiumCommission + feesIncome + financeCommission),
    finance,
  };
}

function calculateFinance(
  totalSellingPrice: number,
  instalments: Instalments,
): CustomerFinanceBreakdown {
  const initialPayment = roundMoney(
    totalSellingPrice * CUSTOMER_PRICING_RULES.instalmentDepositRate + 0.01,
  );
  const loanAmount = roundMoney(totalSellingPrice - initialPayment);
  const transactionFee = roundMoney(
    loanAmount * CUSTOMER_PRICING_RULES.instalmentTransactionFeeRate,
  );
  const financedTotal = roundMoney(loanAmount + transactionFee);

  return {
    instalments,
    firstPaymentTiming:
      instalments === 10 ? "Delay 1 Calendar Month" : "Immediate (Within 5 days)",
    depositRate: CUSTOMER_PRICING_RULES.instalmentDepositRate,
    transactionFeeRate: CUSTOMER_PRICING_RULES.instalmentTransactionFeeRate,
    loanAmount,
    initialPayment,
    transactionFee,
    monthlyPayment: roundMoney(financedTotal / instalments),
    totalPayable: roundMoney(totalSellingPrice + transactionFee),
  };
}

function roundMoney(value: number): number {
  return Number((value || 0).toFixed(2));
}
